// components
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { StyledBoxFlexColumnSpaceBetweenFullHeight } from 'components/shared/Box/Box.styles';
import {
  StyledTypographyNormalLineHeight,
  StyledTypographyNormalLineHeightBold
} from 'components/shared/Typography/Typography.styles';
import {
  Styled32SizeIconContainer,
  StyledBoldWhiteTypography,
  StyledDashboardCalendarImage,
  StyledDashboardWaveImage,
  StyledIntroDashboardGrid,
  StyledIntroDashboardStack,
  StyledIntroDashboardStackCustomWidth
} from './QualityMonitor.styles';

// constants
import { ANIMATION_VARIANTS } from './DashboardMainTile.consts';

// hooks
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';

// styles
import {
  StyledRowStackAlignItemsGap,
  StyledRowStackSmallGap
} from 'components/shared/Stack/Stack.styles';
import { StyledCustomNoPaddingIconButton } from './DashboardMainTile.styles';

// types
import { Employee } from 'redux/types';
import { DashboardMainTileProps } from './DashboardMainTile.types';

// utils
import { capitalize } from 'lodash';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import dayjs from 'dayjs';

export const DashboardMainTile = ({
  count = 0,
  importantTasks
}: DashboardMainTileProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'qualityMonitor'
  });
  const [employee] = useSessionStorage<Employee | null>('employee', null);
  const firstName = capitalize(employee?.nameFirst);
  const isSmallerThanExtraLarge = useMediaQuery((theme) =>
    theme.breakpoints.down('extralarge')
  );
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  const currentHour = dayjs().get('hour');

  const greetingsMessage = (hour) => {
    if (hour > 5 && hour < 12) {
      return t('goodMorning', {
        name: firstName ? firstName : ''
      });
    } else if (hour > 11 && hour < 18) {
      return t('goodAfternoon', {
        name: firstName ? firstName : ''
      });
    } else {
      return t('goodNight', {
        name: firstName ? firstName : ''
      });
    }
  };

  const mappedTasks = useMemo(() => {
    const dashboardStatistics =
      importantTasks?.map(
        (item) =>
          `${item.count} ${t(
            `latestStatistics.${item.key}.${item.definitionKey}`
          )}`
      ) ?? [];

    const openDutiesCount = count && count > 0 && t('openDuties', { count });

    return openDutiesCount
      ? [openDutiesCount, ...dashboardStatistics]
      : [...dashboardStatistics];
  }, [importantTasks, count, t]);

  const handleNavigate = (dir: 1 | -1) => {
    setPaused(true);
    setDirection(dir);
    setIndex((prev) => {
      if (dir === 1) {
        return prev === mappedTasks.length - 1 ? 0 : prev + 1;
      } else {
        return prev === 0 ? mappedTasks.length - 1 : prev - 1;
      }
    });
    setTimeout(() => setPaused(false), 1000);
  };

  useEffect(() => {
    if (mappedTasks.length === 0 || paused) return;

    const interval = setInterval(() => {
      setDirection(1);
      setIndex((prev) => (prev + 1) % mappedTasks.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [mappedTasks, paused]);

  return (
    <StyledIntroDashboardGrid size={isSmallerThanExtraLarge ? 12 : 8}>
      <StyledBoxFlexColumnSpaceBetweenFullHeight>
        <Stack>
          <StyledBoldWhiteTypography variant="h5" color="inherit">
            {greetingsMessage(currentHour)}
          </StyledBoldWhiteTypography>
          <Typography>{t('latestStatistics.title')}</Typography>
        </Stack>

        {mappedTasks?.length > 0 ? (
          <StyledIntroDashboardStack>
            <Styled32SizeIconContainer iconColor="#FFE590" iconSize="LARGE">
              <Icon type="lightbulb-on" color="inherit" />
            </Styled32SizeIconContainer>
            <Box>
              <StyledRowStackAlignItemsGap>
                <StyledTypographyNormalLineHeight variant="h6">
                  {mappedTasks?.length > 1
                    ? t('pointsOfAttention')
                    : t('pointOfAttention')}
                </StyledTypographyNormalLineHeight>
                <StyledRowStackSmallGap className="ticker-rotation-buttons">
                  <StyledCustomNoPaddingIconButton
                    type="chevron-left"
                    color="inherit"
                    size="small"
                    onClick={() => handleNavigate(-1)}
                  />
                  <StyledCustomNoPaddingIconButton
                    type="chevron-right"
                    color="inherit"
                    size="small"
                    onClick={() => handleNavigate(1)}
                  />
                </StyledRowStackSmallGap>
              </StyledRowStackAlignItemsGap>
              <StyledTypographyNormalLineHeightBold variant="h6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={index}
                    custom={direction}
                    variants={ANIMATION_VARIANTS}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5 }}
                    onHoverStart={() => setPaused(true)}
                    onHoverEnd={() => setPaused(false)}
                    onFocus={() => setPaused(true)}
                    onBlur={() => setPaused(false)}
                    tabIndex={0}
                  >
                    {mappedTasks?.[index]}
                  </motion.div>
                </AnimatePresence>
              </StyledTypographyNormalLineHeightBold>
            </Box>
          </StyledIntroDashboardStack>
        ) : (
          <StyledIntroDashboardStackCustomWidth>
            <Styled32SizeIconContainer iconColor="#FFE590" iconSize="LARGE">
              <Icon type="thumbs-up-outline" color="inherit" />
            </Styled32SizeIconContainer>
            <StyledTypographyNormalLineHeight variant="h6">
              <Trans
                i18nKey={t('wellDoneNoOpenTasks')}
                components={{ br: <br /> }}
              />
            </StyledTypographyNormalLineHeight>
          </StyledIntroDashboardStackCustomWidth>
        )}
      </StyledBoxFlexColumnSpaceBetweenFullHeight>
      <StyledDashboardWaveImage
        src={`${window?.reactMetadata?.staticUrl}aysist/styles/external/img/wave-graphics.svg`}
      />

      <StyledDashboardCalendarImage
        src={`${window?.reactMetadata?.staticUrl}aysist/styles/external/img/calendar-graphics.svg`}
      />
    </StyledIntroDashboardGrid>
  );
};
